import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Star, ArrowRight, Coffee } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [shops, setShops] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchShops();
  }, []);

  const fetchShops = async () => {
    try {
      const response = await axios.get(`${API}/shops`);
      setShops(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching shops:', error);
      setLoading(false);
    }
  };

  const filteredShops = shops.filter(shop =>
    shop.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    shop.address.toLowerCase().includes(searchQuery.toLowerCase()) ||
    shop.specialties.some(s => s.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const featuredShops = filteredShops.slice(0, 6);

  return (
    <div className="grain-overlay">
      <div
        className="hero-image relative"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1764964048064-a48368fe7c3e?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/50 to-black/70" />
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <div className="elegant-divider opacity-50 mb-8"></div>
          <h1 className="text-5xl md:text-7xl font-semibold text-white mb-6 leading-tight" data-testid="hero-title">
            Discover the Art of
            <br />
            <span className="italic">Fine Coffee</span>
          </h1>
          <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto mb-10 leading-relaxed font-light">
            Curated collection of Angeles City's most distinguished coffee establishments
          </p>
          <div className="max-w-2xl mx-auto">
            <div className="relative bg-white/95 backdrop-blur-md rounded-lg shadow-2xl p-2">
              <Input
                data-testid="hero-search-input"
                type="text"
                placeholder="Search by name, location, or specialty..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-14 px-6 text-base bg-transparent border-0 focus:ring-0 placeholder:text-muted-foreground/60"
              />
              <Button
                data-testid="hero-search-button"
                className="absolute right-2 top-2 h-14 px-8 bg-accent hover:bg-accent/90 shadow-md"
              >
                Search
              </Button>
            </div>
          </div>
          <div className="elegant-divider opacity-50 mt-8"></div>
        </div>
      </div>

      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-28">
        <div className="text-center mb-16">
          <span className="text-sm font-medium text-accent uppercase tracking-wider">Featured Collection</span>
          <h2 className="text-4xl md:text-5xl font-semibold mt-3 mb-4" data-testid="featured-title">
            Exceptional Cafés
          </h2>
          <p className="text-base text-muted-foreground max-w-2xl mx-auto">
            Handpicked establishments that define coffee excellence in Angeles City
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-muted/50 h-72 rounded mb-4" />
                <div className="bg-muted/50 h-6 w-3/4 rounded mb-2" />
                <div className="bg-muted/50 h-4 w-1/2 rounded" />
              </div>
            ))}
          </div>
        ) : featuredShops.length === 0 ? (
          <div className="text-center py-24">
            <Coffee className="h-16 w-16 text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="text-2xl font-medium mb-2">No coffee shops yet</h3>
            <p className="text-muted-foreground">Check back soon for local cafes in your area</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {featuredShops.map((shop) => (
              <Link key={shop.id} to={`/shop/${shop.id}`} data-testid={`shop-card-${shop.id}`}>
                <Card className="card-hover overflow-hidden bg-card h-full group">
                  <div className="overflow-hidden aspect-[4/3] relative">
                    <img
                      src={shop.image_url}
                      alt={shop.name}
                      className="w-full h-full object-cover image-zoom"
                    />
                    <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full flex items-center gap-1">
                      <Star className="h-3.5 w-3.5 fill-accent text-accent" strokeWidth={0} />
                      <span className="text-sm font-semibold text-foreground">{shop.rating.toFixed(1)}</span>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-semibold mb-3 group-hover:text-accent transition-colors" data-testid={`shop-name-${shop.id}`}>
                      {shop.name}
                    </h3>
                    <div className="flex items-start gap-2 text-sm text-muted-foreground mb-4">
                      <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" strokeWidth={2} />
                      <span className="line-clamp-2">{shop.address}</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {shop.specialties.slice(0, 3).map((specialty, idx) => (
                        <span
                          key={idx}
                          className="text-xs px-3 py-1.5 bg-secondary text-secondary-foreground rounded font-medium"
                        >
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
        
        <div className="text-center mt-16">
          <Link to="/directory">
            <Button
              data-testid="view-all-button"
              size="lg"
              variant="outline"
              className="h-12 px-8 border-2 border-border hover:border-accent hover:text-accent transition-all group"
            >
              View All Coffee Shops
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" strokeWidth={2} />
            </Button>
          </Link>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-24 py-20 bg-muted/20 border-y border-border">
        <div className="max-w-4xl mx-auto text-center">
          <div className="elegant-divider mb-8"></div>
          <h2 className="text-4xl md:text-5xl font-semibold mb-6">
            Every Cup Tells a Story
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed mb-10">
            From meticulously crafted pour-overs to velvety lattes, discover the artistry and passion 
            behind every brew. Support local artisans and find your sanctuary.
          </p>
          <Link to="/directory">
            <Button
              data-testid="explore-directory-button"
              size="lg"
              className="h-14 px-10 bg-accent hover:bg-accent/90 text-accent-foreground font-medium shadow-lg"
            >
              Explore the Collection
            </Button>
          </Link>
          <div className="elegant-divider mt-8"></div>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-24 py-16 bg-background">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">In Partnership With</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center justify-items-center">
            <div className="flex items-center justify-center p-8 bg-card rounded-lg border border-border hover:border-accent/30 transition-all group w-full h-32">
              <div className="text-center">
                <p className="text-2xl font-bold text-accent group-hover:scale-110 transition-transform">Grab</p>
                <p className="text-xs text-muted-foreground mt-1">Food Delivery</p>
              </div>
            </div>
            <div className="flex items-center justify-center p-8 bg-card rounded-lg border border-border hover:border-accent/30 transition-all group w-full h-32">
              <div className="text-center">
                <p className="text-2xl font-bold text-[#D70F64] group-hover:scale-110 transition-transform">foodpanda</p>
                <p className="text-xs text-muted-foreground mt-1">Food Delivery</p>
              </div>
            </div>
            <div className="flex items-center justify-center p-8 bg-card rounded-lg border border-border hover:border-accent/30 transition-all group w-full h-32">
              <div className="text-center">
                <p className="text-2xl font-bold text-[#FF6700] group-hover:scale-110 transition-transform">Lalamove</p>
                <p className="text-xs text-muted-foreground mt-1">Logistics Partner</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;